import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createAppTheme } from "./index";
import {
  readThemeMode,
  writeThemeMode,
  type ThemeModePreference,
} from "./themeModeStorage";

interface AppThemeProviderProps {
  children: ReactNode;
}

/** Effektiver Hell-/Dunkelmodus, nachdem `auto` aufgelöst wurde. */
export type ResolvedThemeMode = "light" | "dark";

interface ThemeModeContextValue {
  /** Gewählte Einstellung des Nutzers (`auto` = System-Einstellung folgen). */
  modePreference: ThemeModePreference;
  /** Aufgelöster Modus (`auto` → System, sonst wie die Einstellung). */
  resolvedMode: ResolvedThemeMode;
  /** Setzt die Einstellung und schreibt sie in den Farbschema-Cache. */
  setModePreference: (mode: ThemeModePreference) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/**
 * Zugang zum Farbschema-Context. Muss innerhalb des `AppThemeProvider`
 * verwendet werden.
 */
export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error("useThemeMode muss innerhalb des AppThemeProvider verwendet werden.");
  }
  return ctx;
}

/**
 * Stellt das zentrale App-Theme bereit. Die Farbschema-Einstellung
 * (`auto`/`light`/`dark`) wird im Farbschema-Cache (`localStorage`)
 * persistiert; `auto` folgt der System-Einstellung
 * (`prefers-color-scheme`). Das Theme wird pro aufgelöstem Modus
 * memoisiert, damit es nicht bei jedem Render neu gebaut wird.
 */
export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [modePreference, setModePreferenceState] =
    useState<ThemeModePreference>(() => readThemeMode() ?? "auto");

  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  const resolvedMode: ResolvedThemeMode =
    modePreference === "auto"
      ? prefersDarkMode
        ? "dark"
        : "light"
      : modePreference;

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  const setModePreference = useCallback((mode: ThemeModePreference) => {
    setModePreferenceState(mode);
    writeThemeMode(mode);
  }, []);

  const contextValue = useMemo(
    () => ({ modePreference, resolvedMode, setModePreference }),
    [modePreference, resolvedMode, setModePreference],
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
